/**
 * Rax Budget Alert Banner
 *
 * Displays a dismissible banner in the chat UI when user approaches or hits their budget cap.
 * Shows warning (yellow) at threshold, danger (red) at cap.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface BudgetAlertState {
  /** Current usage in cents */
  usedCents: number;
  /** Budget limit in cents */
  limitCents: number;
  /** Alert threshold (0-1, e.g., 0.8 = 80%) */
  alertThreshold: number;
  /** Whether to pause agents on cap */
  pauseOnCap: boolean;
  /** List of paused agent IDs (if any) */
  pausedAgentIds?: string[];
}

@customElement("rax-budget-alert-banner")
export class RaxBudgetAlertBanner extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        margin-bottom: var(--space-3);
        font-size: var(--font-sm);
        line-height: 1.4;
        animation: slideIn 0.2s ease-out;
      }
      
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(-8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .banner.warning {
        border: 1px solid rgba(245, 158, 11, 0.5);
        background: rgba(245, 158, 11, 0.12);
        color: #fbbf24;
      }
      
      .banner.danger {
        border: 1px solid rgba(239, 68, 68, 0.55);
        background: rgba(239, 68, 68, 0.14);
        color: #fca5a5;
      }
      
      .banner-content {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex: 1;
      }
      
      .banner-icon {
        font-size: var(--font-lg);
      }
      
      .banner-text {
        flex: 1;
      }
      
      .banner-text strong {
        font-weight: 600;
      }
      
      .banner-actions {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .banner-btn {
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .banner-btn.primary {
        background: var(--rax-yellow);
        border: none;
        color: var(--rax-black);
      }
      
      .banner-btn.primary:hover {
        opacity: 0.9;
      }
      
      .banner-btn.secondary {
        background: transparent;
        border: 1px solid currentColor;
        color: inherit;
        opacity: 0.8;
      }
      
      .banner-btn.secondary:hover {
        opacity: 1;
      }
      
      .banner-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .dismiss-btn {
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        padding: var(--space-1);
        opacity: 0.6;
        font-size: var(--font-lg);
        line-height: 1;
      }
      
      .dismiss-btn:hover {
        opacity: 1;
      }
      
      @media (max-width: 640px) {
        .banner {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }
      
        .banner-actions {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `,
  ];

  @property({ type: Object })
  budget: BudgetAlertState | null = null;

  @property({ type: Boolean })
  dismissible = true;

  @state()
  private dismissed = false;

  @state()
  private resuming = false;

  private get percentUsed(): number {
    if (!this.budget || this.budget.limitCents <= 0) return 0;
    return (this.budget.usedCents / this.budget.limitCents) * 100;
  }

  private get isOverThreshold(): boolean {
    if (!this.budget) return false;
    return this.percentUsed >= this.budget.alertThreshold * 100;
  }

  private get isOverCap(): boolean {
    if (!this.budget) return false;
    return this.budget.usedCents >= this.budget.limitCents;
  }

  private get hasPausedAgents(): boolean {
    return (this.budget?.pausedAgentIds?.length ?? 0) > 0;
  }

  override render() {
    if (this.dismissed || !this.budget || !this.isOverThreshold) {
      return nothing;
    }

    const severity = this.isOverCap ? "danger" : "warning";
    const icon = this.isOverCap ? "🚫" : "⚠️";
    const usedUsd = (this.budget.usedCents / 100).toFixed(2);
    const limitUsd = (this.budget.limitCents / 100).toFixed(2);

    return html`
      <div class="banner ${severity}" role="alert">
        <div class="banner-content">
          <span class="banner-icon">${icon}</span>
          <div class="banner-text">
            ${
              this.isOverCap
                ? html`
                  <strong>Budget cap reached.</strong>
                  You've used $${usedUsd} of your $${limitUsd} monthly limit.
                  ${
                    this.budget.pauseOnCap && this.hasPausedAgents
                      ? html`
                          Active agents have been paused.
                        `
                      : html`
                          New chat turns are blocked until you raise your cap.
                        `
                  }
                `
                : html`
                  <strong>Budget warning:</strong>
                  You've used ${this.percentUsed.toFixed(0)}% ($${usedUsd}) of your $${limitUsd} monthly limit.
                `
            }
          </div>
        </div>
        <div class="banner-actions">
          ${
            this.hasPausedAgents
              ? html`
                <button
                  class="banner-btn secondary"
                  ?disabled=${this.resuming}
                  @click=${this.handleResume}
                >
                  ${this.resuming ? "Resuming..." : "Resume agents"}
                </button>
              `
              : nothing
          }
          <button
            class="banner-btn primary"
            @click=${this.handleManageBudget}
          >
            Manage budget
          </button>
          ${
            this.dismissible
              ? html`
                <button
                  class="dismiss-btn"
                  @click=${this.handleDismiss}
                  aria-label="Dismiss alert"
                >
                  ×
                </button>
              `
              : nothing
          }
        </div>
      </div>
    `;
  }

  private handleDismiss() {
    this.dismissed = true;
    this.dispatchEvent(new CustomEvent("dismiss"));
  }

  private handleManageBudget() {
    this.dispatchEvent(new CustomEvent("manage-budget"));
  }

  private async handleResume() {
    if (this.resuming) return;
    this.resuming = true;

    try {
      const response = await raxApi.resumeSpendPausedAgents();
      if (response.success) {
        this.dispatchEvent(new CustomEvent("agents-resumed"));
      }
    } catch (err) {
      console.error("[BudgetAlertBanner] Failed to resume agents:", err);
    } finally {
      this.resuming = false;
    }
  }

  /** Reset dismissed state (call when budget data refreshes) */
  public resetDismissed() {
    this.dismissed = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-budget-alert-banner": RaxBudgetAlertBanner;
  }
}
